import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Avatar from '../Avatar';
import ChildFormModal from '../ChildFormModal';
import { deleteChild, isCarer } from '../../services/children';
import { nextBirthday, countdownLabel } from '../../utils/dates';

function errText(err, fallback) {
    return err.response?.data?.error || fallback;
}

/**
 * ChildrenTab — the children whose lists live in this group.
 *
 * Mirrors PetsTab, with the one difference that is the whole feature: a child has a SET
 * of carers rather than a single caretaker, so the card shows who they are and any carer
 * can open the profile to add someone else. Deleting stays narrower than editing (the
 * person who added them, or a group admin), because it takes the whole list with it.
 */
export default function ChildrenTab({ group, childrenList, user, isAdmin, onChanged, notify }) {
    const navigate = useNavigate();
    const [modal, setModal] = useState(null); // {} to create, { child } to edit

    const handleDelete = async (e, child) => {
        e.stopPropagation();
        if (!window.confirm(
            `Remove "${child.name}"? Their whole list goes too — including any gifts people have already claimed or bought. This can't be undone.`
        )) return;
        try {
            await deleteChild(child.id);
            notify('success', `${child.name} was removed.`);
            onChanged();
        } catch (err) {
            notify('error', errText(err, 'Could not remove them.'));
        }
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Children in {group.name}</h2>
                <button
                    onClick={() => setModal({})}
                    className="px-4 py-2 bg-brand-600 text-white rounded-md hover:bg-brand-700 text-sm font-medium"
                >
                    + Add a child
                </button>
            </div>

            {childrenList.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg shadow text-gray-500">
                    <p>No children here yet.</p>
                    <p className="text-sm mt-1">
                        Add one to keep a wish list on their behalf — they don&rsquo;t need an
                        account, and you can let other grown-ups add to it too.
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {childrenList.map((child) => {
                        const canEdit = isCarer(child, user?.id);
                        const canDelete = child.created_by_user_id === user?.id || isAdmin;
                        const bday = nextBirthday(child.birthday);
                        return (
                            <div
                                key={child.id}
                                onClick={() => navigate(`/child/${child.id}?group=${group.id}`)}
                                className="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow"
                            >
                                <div className="flex items-center gap-3">
                                    <Avatar
                                        name={child.name}
                                        url={child.avatar_url}
                                        color={child.avatar_color}
                                        seed={child.id}
                                        size={40}
                                    />
                                    <div className="min-w-0">
                                        <p className="font-medium text-gray-900 truncate">{child.name}</p>
                                        {bday && (
                                            <span className="inline-block text-xs px-2 py-0.5 rounded bg-pink-100 text-pink-700">
                                                🎂 {bday.label} · {countdownLabel(bday.daysUntil)}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* Who may add to this list. The point of the feature, so it is
                                    on the card rather than hidden inside the profile. */}
                                <p className="text-xs text-gray-500 mt-3">
                                    Looked after by{' '}
                                    {(child.carers || []).map((c) => c.name).join(', ') || '—'}
                                </p>

                                {(canEdit || canDelete) && (
                                    <div className="flex gap-3 mt-3 text-sm">
                                        {canEdit && (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); setModal({ child }); }}
                                                className="text-brand-600 hover:text-brand-700"
                                            >
                                                Edit
                                            </button>
                                        )}
                                        {canDelete && (
                                            <button
                                                onClick={(e) => handleDelete(e, child)}
                                                className="text-red-600 hover:text-red-700"
                                            >
                                                Remove
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {modal && (
                <ChildFormModal
                    groupId={group.id}
                    child={modal.child}
                    notify={notify}
                    onClose={() => setModal(null)}
                    onSaved={() => { setModal(null); onChanged(); }}
                />
            )}
        </div>
    );
}
